/**
 * ContextStep Component
 * Step 1: Ask where the macro will be used
 */

import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { FileText, MessageSquare, AlignLeft, AlertCircle, Copy, Check } from 'lucide-react'
import { MessageContext, Platform } from '../../../types/macros'

interface ContextStepProps {
  platform: Platform | null
  selectedContext: MessageContext | null
  onSelectContext: (context: MessageContext) => void
  onStartWithTemplate: () => void
}

const ContextStep: React.FC<ContextStepProps> = ({
  platform,
  selectedContext,
  onSelectContext,
  onStartWithTemplate
}) => {
  const isShopify = platform === Platform.SHOPIFY
  const [copiedMacro, setCopiedMacro] = useState<string | null>(null)

  const handleCopyMacro = (macro: string) => {
    navigator.clipboard.writeText(macro)
    setCopiedMacro(macro)
    setTimeout(() => setCopiedMacro(null), 2000)
  }

  const contexts = [
    {
      value: MessageContext.HEADER_TEXT,
      icon: FileText,
      label: 'Header Text',
      description: isShopify 
        ? 'Appears as the main heading of the offer unit'
        : 'Main heading for MomentPerks offers and Shopify integration',
      example: isShopify ? 'Hey {{first_name}}, special offer just for you!' : 'Hey {{firstname}}, special offer just for you!',
      shopifyOnly: false
    },
    {
      value: MessageContext.LEAD_IN_TEXT,
      icon: MessageSquare,
      label: 'Lead-in Text',
      description: isShopify
        ? 'Appears above the offer description'
        : 'Introductory text shown above the offer in MomentPerks',
      example: isShopify ? 'Thank you for your order, {{last_name}} family perks are ready!' : '{{#if orderid}}Order {{orderid}} confirmed{{/if}}',
      shopifyOnly: false
    },
    {
      value: MessageContext.TITLE_TEXT,
      icon: AlignLeft,
      label: 'Title Text',
      description: 'PerksWall title displayed in the top header (when enabled)',
      example: 'Welcome {{firstname}}, explore your rewards!',
      shopifyOnly: true  // Not supported in Shopify
    }
  ]

  // Filter contexts based on platform
  const availableContexts = isShopify 
    ? contexts.filter(c => !c.shopifyOnly)
    : contexts

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-[#1A2B49] mb-2">
          {isShopify ? 'Shopify Macro Personalization' : 'Where will this macro be used?'}
        </h2>
        <p className="text-[#6E6D7A] mb-2">
          {isShopify 
            ? 'Select where you want to use macros in your MomentPerks offer unit. After selection, you can copy the available macros.'
            : 'Macros are supported in MomentPerks, PerksWall, and Shopify integrations.'
          }
        </p>
        {!isShopify && (
          <p className="text-sm text-[#6E6D7A]">
            Select the message type to get started with personalized content.
          </p>
        )}
      </div>



      {/* Context Options */}
      <div className={`grid grid-cols-1 ${isShopify ? 'md:grid-cols-2' : 'md:grid-cols-3'} gap-4`}>
        {availableContexts.map((context) => {
          const Icon = context.icon
          const isSelected = selectedContext === context.value

          return (
            <motion.button
              key={context.value}
              onClick={() => onSelectContext(context.value)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`relative p-6 rounded-lg border-2 transition-all text-left ${
                isSelected
                  ? 'border-[#FE4215] bg-[#FE4215]/5 shadow-md'
                  : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
              }`}
            >
              {/* Icon */}
              <div
                className={`inline-flex p-3 rounded-lg mb-4 ${
                  isSelected ? 'bg-[#FE4215] text-white' : 'bg-gray-100 text-[#233D71]'
                }`}
              >
                <Icon className="w-6 h-6" />
              </div>

              {/* Label & Description */}
              <h3 className="text-lg font-semibold text-[#1A2B49] mb-2">{context.label}</h3>
              <p className="text-sm text-[#6E6D7A] mb-3">{context.description}</p>

              {/* Example */}
              <div className="mt-4 p-2 bg-gray-50 rounded border border-gray-200">
                <p className="text-xs text-gray-500 mb-1">Example:</p>
                <code className="text-xs text-[#233D71] font-mono break-all">
                  {context.example}
                </code>
              </div>

              {/* Selection Indicator */}
              {isSelected && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute top-4 right-4 w-6 h-6 bg-[#FE4215] rounded-full flex items-center justify-center"
                >
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </motion.div>
              )}
            </motion.button>
          )
        })}
      </div>

      {/* Shopify Macro Table - Only show after context selection */}
      {isShopify && selectedContext && (
        <div className="space-y-4 mt-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 shadow-sm">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="text-left px-4 py-3 text-gray-900 dark:text-gray-100 font-semibold">Macro</th>
                  <th className="text-left px-4 py-3 text-gray-900 dark:text-gray-100 font-semibold">Usage</th>
                  <th className="text-left px-4 py-3 text-gray-900 dark:text-gray-100 font-semibold">Example</th>
                  <th className="text-center px-4 py-3 text-gray-900 dark:text-gray-100 font-semibold w-20">Copy</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-gray-200 dark:border-gray-700">
                  <td className="px-4 py-3">
                    <code className="px-2 py-1 bg-gray-100 dark:bg-gray-900 rounded text-xs text-gray-900 dark:text-gray-100 font-mono">
                      {`{{first_name}}`}
                    </code>
                  </td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Customer's first name</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400 italic">Hey Sarah, here's something you'll love!</td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => handleCopyMacro('{{first_name}}')}
                      className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      title="Copy macro"
                    >
                      {copiedMacro === '{{first_name}}' ? (
                        <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                      ) : (
                        <Copy className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      )}
                    </button>
                  </td>
                </tr>
                <tr className="border-t border-gray-200 dark:border-gray-700">
                  <td className="px-4 py-3">
                    <code className="px-2 py-1 bg-gray-100 dark:bg-gray-900 rounded text-xs text-gray-900 dark:text-gray-100 font-mono">
                      {`{{last_name}}`}
                    </code>
                  </td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Customer's last name</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400 italic">Order confirmed, Williams family perks are ready!</td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => handleCopyMacro('{{last_name}}')}
                      className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      title="Copy macro"
                    >
                      {copiedMacro === '{{last_name}}' ? (
                        <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                      ) : (
                        <Copy className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      )}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Limitation Notice */}
          <div className="flex items-start gap-2 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
            <p className="text-sm text-blue-900 dark:text-blue-100">
              <span className="font-semibold">Shopify limitation:</span> Only <code className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/40 rounded text-xs font-mono">first_name</code> and <code className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/40 rounded text-xs font-mono">last_name</code> macros are available. Custom macro creation is not supported.
            </p>
          </div>

          {/* Warning */}
          <div className="flex items-start gap-2 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
            <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
            <p className="text-sm text-amber-900 dark:text-amber-100">
              <span className="font-semibold">Logged-in customers only.</span> Guest checkouts won't show personalized values.
            </p>
          </div>
        </div>
      )}

      {/* Template Quick Start - Only show for non-Shopify */}
      {!isShopify && (
        <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm">
                <span className="text-2xl">⚡</span>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-[#1A2B49] mb-2">Quick Start with Templates</h3>
              <p className="text-sm text-[#6E6D7A] mb-4">
                Not sure what you need? Browse pre-built templates and customize them to fit your needs.
              </p>
              <button
                onClick={onStartWithTemplate}
                className="inline-flex items-center px-4 py-2 bg-[#FE4215] hover:bg-[#E5350A] text-white font-medium rounded-lg transition-colors shadow-sm"
              >
                Browse Templates →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ContextStep
